import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { ReportShell } from "@/features/reports/ReportShell";
import { useLicences, useAllLicenceAlertLogs } from "@/features/compliance/api";
import { licenceStatus, daysRemaining } from "@/features/compliance/types";
import type { ReportFilters } from "@/features/reports/types";
import { useBranch } from "@/context/BranchContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isBranchLocked } from "@/lib/roles";
import { formatDate } from "@/features/assets/format";


export const Route = createFileRoute("/_authenticated/reports/licence-compliance")({
  component: LicenceReport,
});

function LicenceReport() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const { currentBranchId } = useBranch();
  const { data: me } = useCurrentUser();
  const lockedBranchId = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;
  const effectiveBranchId = lockedBranchId ?? filters.branchId ?? currentBranchId ?? null;
  const { data: rows = [], isLoading } = useLicences({});
  const { data: alerts = [] } = useAllLicenceAlertLogs();

  const lastAlert = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of alerts) if (!m.has(a.licence_id)) m.set(a.licence_id, a.sent_at);
    return m;
  }, [alerts]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (filters.status && licenceStatus(r.valid_till) !== filters.status) return false;
      if (effectiveBranchId) {
        // include org-wide licences without an asset; scope asset-linked ones to branch
        if (r.asset && r.asset.branch_id && r.asset.branch_id !== effectiveBranchId) return false;
      }
      return true;
    });
  }, [rows, filters.status, effectiveBranchId]);

  const exportRows = useMemo(() => () => filtered.map((r) => ({
    LicenceType: r.type,
    LicenceNumber: r.licence_no,
    AssetCode: r.asset?.asset_code ?? "",
    AssetName: r.asset?.name ?? "",
    Facility: r.facility_area ?? "",
    IssuingAuthority: r.issuing_authority ?? "",
    IssuedDate: r.issued_date ?? "",
    ValidTill: r.valid_till ?? "",
    DaysRemaining: daysRemaining(r.valid_till),
    Status: licenceStatus(r.valid_till),
    Owner: r.owner?.full_name ?? r.owner?.email ?? "",
    AlertOwner: r.alert_owner?.full_name ?? r.alert_owner?.email ?? "",
    DocumentStatus: r.document_url ? "View" : "Missing",
    LastAlertSent: lastAlert.get(r.id) ?? "",
    Remarks: r.notes ?? "",
  })), [filtered, lastAlert]);

  return (
    <ReportShell id="R-16" title="Licence & Compliance Status"
      description="AERB, PNDT, PCB and other statutory licences."
      filters={filters} onChange={setFilters} enabled={["branchId", "status"]}
      exportRows={exportRows}>
      <Card><CardContent className="p-0">
        {isLoading ? <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Type</TableHead><TableHead>Licence #</TableHead>
              <TableHead>Asset</TableHead><TableHead>Facility</TableHead>
              <TableHead>Authority</TableHead><TableHead>Issued</TableHead>
              <TableHead>Valid Till</TableHead><TableHead>Days</TableHead>
              <TableHead>Status</TableHead><TableHead>Owner</TableHead>
              <TableHead>Doc</TableHead><TableHead>Last Alert</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((r) => {
                const s = licenceStatus(r.valid_till);
                const d = daysRemaining(r.valid_till);
                const la = lastAlert.get(r.id);
                return (
                  <TableRow key={r.id}>
                    <TableCell>{r.type}</TableCell>
                    <TableCell className="font-mono text-xs">{r.licence_no}</TableCell>
                    <TableCell className="text-xs">{r.asset ? `${r.asset.asset_code} · ${r.asset.name}` : "—"}</TableCell>
                    <TableCell className="text-xs">{r.facility_area ?? "—"}</TableCell>
                    <TableCell>{r.issuing_authority ?? "—"}</TableCell>
                    <TableCell>{r.issued_date ?? "—"}</TableCell>
                    <TableCell>{r.valid_till ?? "—"}</TableCell>
                    <TableCell className={`tabular-nums ${d < 0 ? "text-red-600 font-semibold" : d <= 30 ? "text-amber-600 font-semibold" : ""}`}>{d}</TableCell>
                    <TableCell><Badge variant={s === "Expired" ? "destructive" : s === "Expiring Soon" ? "secondary" : "outline"}>{s}</Badge></TableCell>
                    <TableCell className="text-xs">{r.owner?.full_name ?? r.owner?.email ?? "—"}</TableCell>
                    <TableCell>{r.document_url ? <Badge variant="outline">View</Badge> : <Badge variant="outline" className="border-amber-500 text-amber-700">Missing</Badge>}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{la ? formatDate(la) : "—"}</TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && <TableRow><TableCell colSpan={12} className="py-8 text-center text-sm text-muted-foreground">No licences.</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
    </ReportShell>
  );
}
